import { Activity } from "./activity";
import { RollupChildFilter } from "./rollup/rollup_child_filter";
import { RollupRuleEvaluator } from "./rollup/rollup_rule_evaluator";
import { MeasureRollupProcessor, MeasureRollupOptions } from "./rollup/measure_rollup";
import { ObjectiveRollupProcessor } from "./rollup/objective_rollup";
import { ProgressRollupProcessor } from "./rollup/progress_rollup";
import { DurationRollupProcessor } from "./rollup/duration_rollup";
import { CrossClusterProcessor } from "./rollup/cross_cluster_processor";
import {
  GlobalObjectiveSynchronizer,
  GlobalObjective,
  GlobalObjectiveWriteTargets,
} from "./objectives/global_objective_synchronizer";
import { RollupStateValidator } from "./validation/rollup_state_validator";

/**
 * Event callback function type
 */
export type EventCallback = (eventType: string, data?: unknown) => void;

/**
 * Enhanced Rollup Process implementation for SCORM 2004 sequencing
 * Priority 5 Gap: Comprehensive rollup with global objective mapping and complex weighting
 *
 * This class serves as the orchestrator for all rollup operations, delegating
 * specific functionality to specialized processor classes:
 *
 * - RollupChildFilter: Child activity filtering for rollup
 * - RollupRuleEvaluator: Rollup rule evaluation
 * - MeasureRollupProcessor: Measure (score) rollup
 * - ObjectiveRollupProcessor: Objective satisfaction rollup
 * - ProgressRollupProcessor: Progress (completion) rollup
 * - DurationRollupProcessor: Duration aggregation rollup
 * - GlobalObjectiveSynchronizer: Global objective synchronization
 * - RollupStateValidator: State consistency validation
 * - CrossClusterProcessor: Cross-cluster dependency handling
 *
 * @spec SN Book: RB.1.5 (Overall Rollup Process)
 */
export class RollupProcess {
  /**
   * Capability flag for hosts that supported older rollup behavior with a compatibility layer.
   * Rollup contribution controls belong to an activity as a child of its parent; they do not gate
   * calculation of the activity's own rolled-up state.
   */
  public readonly childContributionControlsApplyToParent = true;

  private childFilter: RollupChildFilter;
  private ruleEvaluator: RollupRuleEvaluator;
  private measureProcessor: MeasureRollupProcessor;
  private objectiveProcessor: ObjectiveRollupProcessor;
  private progressProcessor: ProgressRollupProcessor;
  private durationProcessor: DurationRollupProcessor;
  private globalObjectiveSynchronizer: GlobalObjectiveSynchronizer;
  private stateValidator: RollupStateValidator;
  private crossClusterProcessor: CrossClusterProcessor;
  private eventCallback: EventCallback | null;

  /**
   * Create a new RollupProcess orchestrator
   *
   * @param eventCallback - Optional callback for firing events
   */
  constructor(eventCallback?: EventCallback) {
    this.eventCallback = eventCallback || null;

    // Initialize all processors with proper dependency injection
    this.childFilter = new RollupChildFilter();
    this.ruleEvaluator = new RollupRuleEvaluator(this.childFilter);
    this.measureProcessor = new MeasureRollupProcessor(this.childFilter, eventCallback);
    this.objectiveProcessor = new ObjectiveRollupProcessor(
      this.childFilter,
      this.ruleEvaluator,
      eventCallback,
    );
    this.progressProcessor = new ProgressRollupProcessor(
      this.childFilter,
      this.ruleEvaluator,
      this.objectiveProcessor,
      eventCallback,
    );
    this.durationProcessor = new DurationRollupProcessor(eventCallback);
    this.globalObjectiveSynchronizer = new GlobalObjectiveSynchronizer(eventCallback);
    this.stateValidator = new RollupStateValidator(this.childFilter, eventCallback);
    this.crossClusterProcessor = new CrossClusterProcessor(
      this.measureProcessor,
      this.objectiveProcessor,
      this.progressProcessor,
      eventCallback,
    );
  }

  /**
   * Overall Rollup Process
   * Performs rollup from a given activity up through its ancestors
   * OPTIMIZATION: Stops propagating rollup when status stops changing (SCORM 2004 4.6.1)
   *
   * @spec SN Book: RB.1.5 (Overall Rollup Process)
   * @param activity - The activity to start rollup from
   * @returns Array of activities that had status changes
   */
  public overallRollupProcess(
    activity: Activity,
    globalObjectives?: Map<string, GlobalObjective>,
  ): Activity[] {
    const affectedActivities: Activity[] = [];
    let currentActivity: Activity | null = activity.parent; // Start from parent, not the activity itself
    let onlyDurationRollup = false;
    let isFirst = true;

    // Process rollup up the tree from parent to root
    while (currentActivity) {
      // Duration rollup happens FIRST, ALWAYS for cluster activities
      // This happens even when optimization is active for other rollup types
      if (currentActivity.children.length > 0) {
        this.durationProcessor.durationRollupProcess(currentActivity);
      }

      if (!onlyDurationRollup) {
        // Capture status BEFORE rollup
        const beforeStatus = currentActivity.captureRollupStatus();

        // Step 1: Measure Rollup Process (RB.1.1)
        if (currentActivity.children.length > 0) {
          const clusters = this.measureProcessor.measureRollupProcess(currentActivity);
          // Step 1b: Completion Measure Rollup Process (RB.1.1 b)
          this.measureProcessor.completionMeasureRollupProcess(currentActivity);

          // Process cross-cluster dependencies if dealing with multiple clusters
          if (clusters.length > 1) {
            this.crossClusterProcessor.processCrossClusterDependencies(currentActivity, clusters);
          }
        }

        // The rollupObjectiveSatisfied and rollupProgressCompletion controls describe whether
        // this activity contributes to its parent's rollup; they do not disable calculation of
        // this activity's own rolled-up state. The child-filtering subprocess applies those
        // controls to each contributing child (SCORM 2004 RB.1.4.2).

        // Step 2: Objective Rollup Process (RB.1.2)
        this.objectiveProcessor.objectiveRollupProcess(currentActivity);

        // Step 3: Activity Progress Rollup Process (RB.1.3)
        this.progressProcessor.activityProgressRollupProcess(currentActivity);

        // A primary objective read map is the activity's view of the shared objective. Restore
        // that view after calculating this cluster and before its parent evaluates the cluster as
        // a rollup child. Without this step, a later descendant rollup can replace a previously
        // satisfied test-out objective with the descendant aggregate even though the mapped
        // global objective remains satisfied.
        // @spec SCORM 2004 4th Ed. SN 3.10.3 and RB.1.5
        if (globalObjectives) {
          this.globalObjectiveSynchronizer.syncGlobalObjectivesReadPhase(
            currentActivity,
            globalObjectives,
          );
        }

        // Capture status AFTER rollup
        const afterStatus = currentActivity.captureRollupStatus();

        // OPTIMIZATION: Check if anything changed (skip first iteration)
        // The first activity always gets processed regardless of change
        if (!isFirst) {
          const changed = !Activity.compareRollupStatus(beforeStatus, afterStatus);
          if (!changed) {
            // No changes detected - activate optimization
            this.eventCallback?.("rollup_optimization_activated", {
              activityId: currentActivity.id,
              depth: affectedActivities.length,
            });
            onlyDurationRollup = true;
          }
        }

        // Add to affected activities if status changed or is first iteration
        if (isFirst || !Activity.compareRollupStatus(beforeStatus, afterStatus)) {
          affectedActivities.push(currentActivity);
        }
      }

      // Move up the tree
      currentActivity = currentActivity.parent;
      isFirst = false;
    }

    return affectedActivities;
  }

  // ============================================================================
  // Public API Methods - Delegate to specialized processors
  // ============================================================================

  /**
   * Validate rollup state consistency across the activity tree
   *
   * @param rootActivity - The root activity to validate from
   * @returns True if state is consistent, false otherwise
   */
  public validateRollupStateConsistency(rootActivity: Activity): boolean {
    return this.stateValidator.validateRollupStateConsistency(rootActivity);
  }

  /**
   * Process global objective mapping for shared objectives
   *
   * @param activity - The root activity to start processing from
   * @param globalObjectives - Global objective map
   */
  public processGlobalObjectiveMapping(
    activity: Activity,
    globalObjectives: Map<string, GlobalObjective>,
  ): void {
    const changedActivities = this.globalObjectiveSynchronizer.processGlobalObjectiveMapping(
      activity,
      globalObjectives,
    );
    const rolledUpParents = new Set<Activity>();

    const activityDepth = (candidate: Activity): number => {
      let depth = 0;
      let parent = candidate.parent;
      while (parent) {
        depth += 1;
        parent = parent.parent;
      }
      return depth;
    };

    // Descendant propagation can roll up through an activity whose own primary
    // objective was populated by a read map. Process deepest-first, then refresh
    // each directly mapped activity before propagating it so that mapped state
    // remains authoritative over the intermediate descendant aggregate.
    // @spec SCORM 2004 4th Ed. TR OB-03c / SN 3.10.3 and RB.1.1
    changedActivities.sort((left, right) => activityDepth(right) - activityDepth(left));

    // @spec SCORM 2004 4th Ed. SN 3.10.3 and RB.1.5 - read-mapped primary
    // objective state participates in rollup before sequencing rules use the result.
    for (const changedActivity of changedActivities) {
      // @spec SCORM 2004 4th Ed. SN 3.13.1 Tracked - an untracked activity
      // exposes read-mapped state to its SCO but does not participate in rollup.
      if (!changedActivity.sequencingControls.tracked) {
        continue;
      }
      this.globalObjectiveSynchronizer.syncGlobalObjectivesReadPhase(
        changedActivity,
        globalObjectives,
      );
      const parent = changedActivity.parent;
      if (parent && !rolledUpParents.has(parent)) {
        this.overallRollupProcess(changedActivity, globalObjectives);
        rolledUpParents.add(parent);
      }
    }
  }

  /**
   * Write the final objective status for one terminating activity before the
   * tree-wide read phase runs.
   *
   * @spec SCORM 2004 SN 4th Ed. SM.7 Objective Map write timing
   */
  public syncTerminatedActivityObjectives(
    activity: Activity,
    globalObjectives: Map<string, GlobalObjective>,
  ): GlobalObjectiveWriteTargets {
    return this.globalObjectiveSynchronizer.syncTerminatedActivityWritePhase(
      activity,
      globalObjectives,
    );
  }

  /**
   * Restore an activity's read-mapped objective state after new-attempt initialization.
   *
   * DB.2 initializes attempt-scoped local objectives, while objective maps remain available
   * across activities. Reapply the read phase before sequencing rules and navigation validity
   * inspect the delivered attempt.
   *
   * @spec SCORM 2004 SN 4th Ed. DB.2 and 3.10.3 Objective Map read timing
   */
  public syncActivityObjectivesFromGlobals(
    activity: Activity,
    globalObjectives: Map<string, GlobalObjective>,
  ): boolean {
    return this.globalObjectiveSynchronizer.syncGlobalObjectivesDeliveryReadPhase(
      activity,
      globalObjectives,
    );
  }

  /**
   * Apply a terminating descendant's fresh objective writes to an active ancestor.
   *
   * @spec SCORM 2004 SN 4th Ed. SM.7 Objective Map write timing
   */
  public syncFreshlyWrittenObjectivesToActiveAncestor(
    activity: Activity,
    globalObjectives: Map<string, GlobalObjective>,
    writeTargets: GlobalObjectiveWriteTargets,
  ): boolean {
    return this.globalObjectiveSynchronizer.syncFreshlyWrittenGlobalObjectivesReadPhase(
      activity,
      globalObjectives,
      writeTargets,
    );
  }

  /**
   * Calculate complex weighted measure for an activity
   *
   * @param activity - The parent activity
   * @param children - Child activities to weight
   * @param options - Configuration options
   * @returns Calculated weighted measure
   */
  public calculateComplexWeightedMeasure(
    activity: Activity,
    children: Activity[],
    options?: MeasureRollupOptions,
  ): number {
    return this.measureProcessor.calculateComplexWeightedMeasure(activity, children, options);
  }

  /**
   * Handle cross-cluster dependencies in rollup
   *
   * @param activity - The activity to process
   * @param clusters - Related activity clusters
   */
  public processCrossClusterDependencies(activity: Activity, clusters: Activity[]): void {
    this.crossClusterProcessor.processCrossClusterDependencies(activity, clusters);
  }

  // ============================================================================
  // Accessor methods for individual processors (for advanced use cases)
  // ============================================================================

  /**
   * Get the child filter processor
   */
  public getChildFilter(): RollupChildFilter {
    return this.childFilter;
  }

  /**
   * Get the rule evaluator processor
   */
  public getRuleEvaluator(): RollupRuleEvaluator {
    return this.ruleEvaluator;
  }

  /**
   * Get the measure rollup processor
   */
  public getMeasureProcessor(): MeasureRollupProcessor {
    return this.measureProcessor;
  }

  /**
   * Get the objective rollup processor
   */
  public getObjectiveProcessor(): ObjectiveRollupProcessor {
    return this.objectiveProcessor;
  }

  /**
   * Get the progress rollup processor
   */
  public getProgressProcessor(): ProgressRollupProcessor {
    return this.progressProcessor;
  }

  /**
   * Get the duration rollup processor
   */
  public getDurationProcessor(): DurationRollupProcessor {
    return this.durationProcessor;
  }

  /**
   * Get the global objective synchronizer
   */
  public getGlobalObjectiveSynchronizer(): GlobalObjectiveSynchronizer {
    return this.globalObjectiveSynchronizer;
  }

  /**
   * Get the state validator
   */
  public getStateValidator(): RollupStateValidator {
    return this.stateValidator;
  }

  /**
   * Get the cross-cluster processor
   */
  public getCrossClusterProcessor(): CrossClusterProcessor {
    return this.crossClusterProcessor;
  }
}
